import * as React from 'react'
import { cn } from '@/lib/utils.js'

/**
 * Surfaces and page furniture.
 *
 * `Card` is the raised surface everything sits on. `PageHeader` exists so that
 * every screen's title, description and actions land in the same place at the
 * same size — nine screens each inventing their own header is the fastest way
 * for a product to look assembled rather than designed.
 */

export function Card ({ className, ...props }) {
  return (
    <div
      className={cn('rounded-lg border border-line bg-surface shadow-card', className)}
      {...props}
    />
  )
}

export function CardHeader ({ className, ...props }) {
  return <div className={cn('flex flex-col gap-1 border-b border-line px-4 py-3 sm:px-5', className)} {...props} />
}

export function CardTitle ({ className, ...props }) {
  return <h2 className={cn('text-sm font-semibold text-ink', className)} {...props} />
}

export function CardBody ({ className, ...props }) {
  return <div className={cn('px-4 py-4 sm:px-5', className)} {...props} />
}

export function CardFooter ({ className, ...props }) {
  return (
    <div
      className={cn('flex flex-wrap items-center gap-2 border-t border-line px-4 py-3 sm:px-5', className)}
      {...props}
    />
  )
}

/**
 * @param {object} props
 * @param {string} props.title
 * @param {React.ReactNode} [props.description]
 * @param {React.ReactNode} [props.actions]
 * @param {React.ReactNode} [props.back] a back link, for detail screens
 */
export function PageHeader ({ title, description, actions, back, className }) {
  return (
    <header className={cn('mb-5 sm:mb-6', className)}>
      {back}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink sm:text-2xl">{title}</h1>
          {description && (
            <div className="mt-1 text-sm text-ink-muted">{description}</div>
          )}
        </div>
        {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
      </div>
    </header>
  )
}

/**
 * A label/value pair. Used across every detail screen, so the alignment and
 * the em-dash-for-empty convention are decided once rather than per screen.
 */
export function DetailRow ({ label, children, className }) {
  return (
    <div className={cn('flex flex-col gap-0.5 sm:flex-row sm:gap-4', className)}>
      <dt className="w-40 shrink-0 text-sm text-ink-muted">{label}</dt>
      <dd className="min-w-0 flex-1 text-sm text-ink">{children ?? '—'}</dd>
    </div>
  )
}
